/**
 * RiskFormModal — wraps the shared FormModal with the Step 6 risk
 * field schema. Four external option sources are injected at render
 * time:
 *
 *   - `assetOptions`      — Step 2 assets
 *   - `threatOptions`     — Step 4 threats
 *   - `scenarioOptions`   — Step 3 damage scenarios
 *   - `attackPathOptions` — Step 5 attack paths (optional FK)
 */
import React, { useMemo } from 'react';
import FormModal from '../../../../components/ui/FormModal/FormModal';
import { RISK_FORM_FIELDS, type RiskRecord } from './riskFieldSchema';

const FormModalAny = FormModal as unknown as React.ComponentType<any>;

export interface OptionLite {
  value: string;
  label: string;
}

export interface RiskFormModalProps {
  mode: 'add' | 'edit';
  initialRisk?: RiskRecord | null;
  assetOptions: OptionLite[];
  threatOptions: OptionLite[];
  scenarioOptions: OptionLite[];
  attackPathOptions: OptionLite[];
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}

export default function RiskFormModal({
  mode,
  initialRisk,
  assetOptions,
  threatOptions,
  scenarioOptions,
  attackPathOptions,
  onSubmit,
  onClose,
}: RiskFormModalProps) {
  const isEdit = mode === 'edit';

  // Normalise to `damage_id` for the form (older rows use damage_id;
  // newer rows use damage_scenario_id — the form modal exposes one
  // field, the consumer's handleSave decides which key to write).
  const initialValues = useMemo(() => {
    if (!initialRisk) return {};
    const { damage_scenario_id, damage_id, ...rest } = initialRisk as Record<string, unknown>;
    return {
      ...rest,
      damage_id: damage_scenario_id ?? damage_id ?? '',
    };
  }, [initialRisk]);

  const fields = useMemo(
    () =>
      RISK_FORM_FIELDS.map((f) => {
        if (f.name === 'asset_id') return { ...f, options: assetOptions };
        if (f.name === 'threat_id') return { ...f, options: threatOptions };
        if (f.name === 'damage_id') return { ...f, options: scenarioOptions };
        if (f.name === 'attack_path_id') return { ...f, options: attackPathOptions };
        if (isEdit && f.name === 'risk_id') return { ...f, disabled: true };
        return f;
      }),
    [assetOptions, threatOptions, scenarioOptions, attackPathOptions, isEdit],
  );

  return (
    <FormModalAny
      title={isEdit ? 'Edit Risk' : 'Add Risk'}
      fields={fields}
      initialValues={initialValues}
      submitLabel={isEdit ? 'Save Changes' : 'Add Risk'}
      submittingLabel={isEdit ? 'Saving…' : 'Adding…'}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth={760}
    />
  );
}
